(ns machtfit.dashboard.stepchall.form.route
  (:require [clojure.string :as str]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.dashboard.stepchall.form.route-editor.core :as route-editor]
            [machtfit.dashboard.stepchall.form.route-editor.path :as route-editor.path]
            [machtfit.dashboard.stepchall.form.route-editor.point-weight :as route-editor.point-weight]
            [machtfit.translation :refer [tn]]
            [re-frame.core :as rf]))

(defn add-random-id [stage]
  (assoc stage :id (random-uuid)))

(defn sort-stages [stages]
  (vec (sort-by (juxt (comp nil? :num-steps) :num-steps) stages)))

(defn- update-goal [{:keys [stages] :as config}]
  ;; set the goal based on the last stage with numeric num-steps,
  ;; but default to 1 and make sure it is at least 1, so we avoid
  ;; situations where start and goal are 0 and the total length is 0
  (assoc config :goal (-> (keep :num-steps stages)
                          last
                          (or 1)
                          (max 1))))

(rf/reg-event-db ::add-stage
  (fn [db _]
    (-> db
        (update-in [:challenge :config :stages] conj (add-random-id {}))
        (update-in [:challenge :config] update-goal))))

(defn- stage-index [stages stage-id]
  (first (keep-indexed (fn [index {:keys [id]}]
                         (when (= id stage-id)
                           index))
                       stages)))

(rf/reg-event-db ::delete-stage
  (fn [db [_ stage-id]]
    (let [stages (-> db :challenge :config :stages)
          index (stage-index stages stage-id)]
      (-> db
          (assoc-in [:challenge :config :stages]
                    (into (subvec stages 0 index)
                          (subvec stages (inc index))))
          (update-in [:challenge :config] update-goal)
          (route-editor.path/remove-stage-point-by-index index)))))

(rf/reg-event-db ::set-stage-name
  (fn [db [_ stage-id language value]]
    (let [stages (-> db :challenge :config :stages)
          index (stage-index stages stage-id)]
      (assoc-in db [:challenge :config :stages index :name language] value))))

(rf/reg-event-db ::set-stage-num-steps
  (fn [db [_ stage-id value]]
    (let [stages (-> db :challenge :config :stages)
          index (stage-index stages stage-id)]
      (-> db
          (assoc-in [:challenge :config :stages index :num-steps] value)
          (update-in [:challenge :config :stages] sort-stages)
          (update-in [:challenge :config] update-goal)
          route-editor.point-weight/calculate))))

(rf/reg-sub ::stage-by-id
  :<- [:get [:challenge :config :stages]]

  (fn [stages [_ stage-id]]
    (let [index (stage-index stages stage-id)
          start-stage-index (->> stages
                                 (keep-indexed (fn [index {:keys [num-steps]}]
                                                 (when (zero? num-steps)
                                                   index)))
                                 first)
          start-stage? (= index start-stage-index)]
      {:stage (get stages index)
       :start-stage? start-stage?
       :deletable? (and
                    (> (count stages) 2)
                    (not start-stage?))})))

(defn- stage [stage-id]
  (let [{{:keys [num-steps]
          :as stage-data} :stage
         num-steps-disabled? :start-stage?
         deletable? :deletable?} @(rf/subscribe [::stage-by-id stage-id])
        {:keys [singular plural]} @(rf/subscribe [:get [:challenge :config :main-unit]])
        unit-str (tn singular plural num-steps)]
    [:tr
     [:td
      [widgets/inline-input
       {:required? true
        :disabled? num-steps-disabled?
        :value num-steps
        :type widgets/type-positive-number-or-nil
        :on-save (when-not num-steps-disabled?
                   #(rf/dispatch [::set-stage-num-steps stage-id %]))
        :style {:width "5.5rem"
                :margin-right "0.5em"
                :text-decoration "inherit"}}]
      (if-not (str/blank? unit-str)
        unit-str
        (tn {:en "Step"
             :de "Schritt"}
            {:en "Steps"
             :de "Schritte"}
            num-steps))]
     [:td
      [widgets/inline-input
       {:required? true
        :value (get-in stage-data [:name :de])
        :on-save #(rf/dispatch [::set-stage-name stage-id :de %])
        :style {:width "5.5rem"
                :text-align "left"
                :text-decoration "inherit"}}]]
     [:td
      [widgets/inline-input
       {:required? true
        :value (get-in stage-data [:name :en])
        :on-save #(rf/dispatch [::set-stage-name stage-id :en %])
        :style {:width "5.5rem"
                :text-align "left"
                :text-decoration "inherit"}}]]
     [:td (when deletable?
            [:a {:style {:font-size "0.8rem" :cursor "pointer"}
                 :on-click #(rf/dispatch [::delete-stage stage-id])}
             "Löschen"])]]))

(rf/reg-event-db ::set-route
  (fn [db [_ new-route]]
    (let [routes (-> db :io :routes)
          route (first (filter
                        (fn [route]
                          (= (:name route) new-route))
                        routes))]
      (-> db
          (assoc-in [:challenge :theme :path] (:path route))
          (assoc-in [:challenge :theme :name] (:name route))
          (assoc-in [:challenge :route :id] (:id route))
          (assoc-in [:challenge :route :background-image :url] (get-in route [:background-image :url]))))))

(defn- routes []
  [:div {:style {:margin-bottom "1rem"}}
   [widgets/label {:id "routes-select"
                   :label-text "Routen Hintergründe"
                   :value ""
                   :label-style {:width "15em"
                                 :display "inline-block"}}]
   [widgets/radio-options {:current-value (or (:name @(rf/subscribe [:get [:challenge :theme]]))
                                              "")
                           :on-select #(rf/dispatch [::set-route %])
                           :choices (map (fn [{:keys [name]}]
                                           [name name])
                                         @(rf/subscribe [:get [:io :routes]]))}]])

(rf/reg-sub ::stage-ids
  :<- [:get [:challenge :config :stages]]

  (fn [stages _]
    (map :id stages)))

(defn section-route []
  [widgets/section
   {:anchor "route"
    :title "Route"
    :content [:<>
              [:div.col.s6
               [:table.table.table-bordered {:style {:margin-bottom "1rem"}}
                [:thead
                 [:tr
                  [:th "Zwischenziel"]
                  [:th "Deutsch"]
                  [:th "Englisch"]
                  [:th {:style {:width "4em"}}]]]
                (into [:tbody]
                      (map (fn [stage-id]
                             ^{:key stage-id} [stage stage-id]))
                      @(rf/subscribe [::stage-ids]))
                [:tfoot>tr>td {:col-span 5} [:a.btn.btn-success {:on-click #(rf/dispatch [::add-stage])}
                                             [:i.icon-plus] " Zwischenziel hinzufügen"]]]
               [routes]]
              [route-editor/route-editor]]}])
